import type { AccountView, BossView, CharacterView, HuntView, Settlement, WorldView } from './types.js';
import { translateError } from './errors.js';
import { pushWorldDrop, type WorldDropView } from './worldDrop.js';
import { isTradeEvent, pushTradeEvent } from './trade.js';

function currentLocale(): string {
  try {
    return localStorage.getItem('tibia-idle.locale') ?? 'pt';
  } catch {
    return 'pt';
  }
}

/**
 * Thin wrapper over the REST API.
 *
 * The token lives in localStorage rather than a cookie: the API is stateless
 * bearer auth and the client may be served from a different origin than the
 * server during development.
 */

const TOKEN_KEY = 'tibia-idle.token';

export interface TownPlayerView {
  id: number;
  name: string;
  level: number;
  vocationId: number;
  appearance: { outfit: number; head: number; body: number; legs: number; feet: number; aura?: number; mount?: number; addons?: number } | null;
  /** Tile its client last reported (synced town). */
  x?: number;
  y?: number;
  z?: number;
  /** 0 north, 1 east, 2 south, 3 west. */
  dir?: number;
  /** In my party. */
  party?: boolean;
}

export interface TownSyncView {
  channel: number;
  channels: Array<{ id: number; count: number; cap: number }>;
  players: TownPlayerView[];
  /** Lines said on the town's Local channel near the viewer (over the head). */
  says?: Array<{ seq: number; id: number; name: string; text: string; at: number }>;
  party: import('./types.js').PartyView | null;
  invites: import('./types.js').PartyInviteView[];
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export function storedToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const token = storedToken();
  const response = await fetch(url, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  let payload: Record<string, unknown> = {};
  if (text) {
    try {
      payload = JSON.parse(text) as Record<string, unknown>;
    } catch {
      payload = {};
    }
  }
  if (!response.ok) {
    const raw = translateError(String(payload['error'] ?? ''), currentLocale());
    const fallback = response.status === 502 || response.status === 500
      ? 'Servidor offline. Aguarde alguns segundos ou reinicie com pnpm dev.'
      : response.status === 404 && url.startsWith('/api/')
        ? 'API indisponível na porta 3000. Pare o servidor antigo e rode: pnpm --filter @tibia-idle/server dev'
        : response.statusText;
    throw new ApiError(raw || fallback, response.status);
  }
  return payload as T;
}

export interface HealthView {
  ok: boolean;
  tickMs: number;
  maxOfflineHours: number;
  content: Record<string, number>;
  generatedAt: string;
  beta: 'open' | 'closed';
  /** Password reset e-mails can be sent. */
  mail?: boolean;
}

const REF_KEY = 'tibia-idle.ref';

/** Reference link code of a character name: spaces become underscores. */
export function referralLinkCode(name: string): string {
  return name.trim().replace(/ /g, '_');
}

/**
 * The referral code from a `/?r=<Character_Name>` link (reference), or an old
 * `/?ref=<username>` one. Kept in localStorage so it survives the visitor
 * browsing around before signing up.
 */
export function referralCode(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get('r') ?? params.get('ref'))?.trim();
    if (fromUrl) localStorage.setItem(REF_KEY, fromUrl.slice(0, 40));
    return localStorage.getItem(REF_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export interface ReferralView {
  /** The first character's link code (null before there is one) — never the account / e-mail. */
  code: string | null;
  percent: number;
  referred: number;
  earnedCoins: number;
  /** `referred`: the friend's character name; null = no character yet. */
  recent: Array<{ referred: string | null; coins: number; createdAt: number }>;
}

export const api = {
  health: () => request<HealthView>('GET', '/api/health'),

  /** Sign-up by e-mail (the server makes the account's internal name from it). */
  register: (email: string, password: string, extras: { invite?: string; acceptRules: boolean }) =>
    request<{ token: string; accountId: number; username: string }>('POST', '/api/register', {
      email, password, invite: extras.invite, acceptRules: extras.acceptRules, ref: referralCode(),
    }),

  /** "Esqueceu sua senha?" — `mail: false` means SMTP is not set up yet (ask on Discord). */
  forgotPassword: (identifier: string) =>
    request<{ ok: boolean; mail: boolean }>('POST', '/api/forgot-password', { identifier }),

  resetTokenValid: (token: string) =>
    request<{ valid: boolean }>('GET', `/api/reset-password?token=${encodeURIComponent(token)}`),

  resetPassword: (token: string, password: string) =>
    request<{ token: string; accountId: number; username: string }>('POST', '/api/reset-password', { token, password }),

  setEmail: (email: string) => request<{ email: string | null }>('POST', '/api/account/email', { email }),

  referral: () => request<ReferralView>('GET', '/api/referral'),

  /** Sign-in by e-mail. */
  login: (email: string, password: string) =>
    request<{ token: string; accountId: number; username: string }>('POST', '/api/login', { email, password }),

  logout: () => request<{ ok: boolean }>('POST', '/api/logout'),

  characters: () => request<{ characters: CharacterView[]; account: AccountView }>('GET', '/api/characters'),

  /** Name (and optional sex) only: every new character starts as a level 1 Rookie. */
  createCharacter: (name: string, extras?: { gender?: 'm' | 'f' }) =>
    request<{ character: CharacterView }>('POST', '/api/characters', { name, ...extras }),

  /** "Excluir personagem": immediate and final; the server checks the typed name. */
  deleteCharacter: (characterId: number, confirmName: string) =>
    request<{ ok: true }>('POST', '/api/characters/delete', { characterId: String(characterId), confirmName }),

  character: (id: number) =>
    request<{ character: CharacterView; settlement: Settlement }>('GET', `/api/characters/${id}`),

  hunts: (id: number) => request<{ hunts: HuntView[] }>('GET', `/api/characters/${id}/hunts`),

  bosses: (id: number) => request<{ bosses: BossView[] }>('GET', `/api/characters/${id}/bosses`),

  startHunt: (id: number, huntId: string, hours?: number) =>
    request<{ character: CharacterView }>('POST', `/api/characters/${id}/hunt`, { huntId, hours }),

  stopHunt: (id: number) =>
    request<{ character: CharacterView; goldBanked: number; supplyRefund: number }>(
      'DELETE',
      `/api/characters/${id}/hunt`,
    ),

  upgradeGear: (id: number) =>
    request<{ character: CharacterView; spent: number }>('POST', `/api/characters/${id}/gear`),

  sellPouch: (id: number) =>
    request<{ character: CharacterView; gold: number }>('POST', `/api/characters/${id}/sell`),

  stashPouch: (id: number) =>
    request<{ character: CharacterView; items: number }>('POST', `/api/characters/${id}/stash`),

  act: (id: number, body: Record<string, unknown>) =>
    request<{ character: CharacterView } & Record<string, unknown>>('POST', `/api/characters/${id}/act`, body),

  world: (channel = 'geral') => request<WorldView>('GET', `/api/world?channel=${encodeURIComponent(channel)}`),

  /** Reference highscores: one board page (20 rows of the top 100). */
  highscores: (category: string, vocation: number | null, page: number) => request<{
    category: string;
    vocation: number | null;
    page: number;
    pageCount: number;
    entries: Array<{ rank: number; id: number; name: string; vocation: number; level: number; value: number }>;
  }>('GET', `/api/highscores?category=${encodeURIComponent(category)}&page=${page}${vocation === null ? '' : `&vocation=${vocation}`}`),

  /** Online players standing in town (not hunting), for the town scene. */
  town: (excludeId?: number) => request<{ players: TownPlayerView[] }>('GET', `/api/town${excludeId ? `?exclude=${excludeId}` : ''}`),
  /** Synced town: report my tile, get my channel, the players on it, my party and invitations. */
  townSync: (characterId: number, position: { x: number; y: number; z: number; dir: number } | null) =>
    request<TownSyncView>('POST', '/api/town/sync', { characterId, ...(position ?? {}) }),
  townChannel: (characterId: number, channel: number) => request<TownSyncView>('POST', '/api/town/channel', { characterId, channel }),

  admin: () => request<{
    /** `coins`: the account's balance (each character row shows the same). */
    accounts: Array<{ id: number; username: string; admin: boolean; coins: number; characters: Array<{ id: number; name: string; level: number; gold: number; coins: number }> }>;
    orders: Array<{ id: number; accountId: number; packId: string; coins: number; brl: number; status: string; createdAt: number }>;
    event: { name: string; experience: number; loot: number };
    metrics: {
      accounts: number;
      characters: number;
      hunting: number;
      queued: number;
      gold: number;
      coins: number;
      beta: string;
      last24h: { dau: number; registers: number; huntsStarted: number; huntsStopped: number; goldFromHunts: number };
      retention: { d1Cohort: number; d1Returned: number; d1: number | null };
      topHunts: Array<{ huntId: string; n: number }>;
    };
    invites: Array<{ code: string; usedBy: number | null }>;
    hunts?: AdminHuntRow[];
  }>('GET', '/api/admin'),

  adminAct: (body: Record<string, unknown>) => request<Record<string, unknown>>('POST', '/api/admin', body),
};

/** Admin panel: a hunt and whether players can start it (false = "Em breve"). */
export interface AdminHuntRow {
  id: string;
  name: string;
  ourName: string;
  ordinal: number | null;
  released: boolean;
  releasedByDefault: boolean;
}

/**
 * Live state feed.
 *
 * Reconnects with a backoff instead of giving up: an idle game is left open for
 * hours, and a laptop that sleeps should pick the session back up on wake
 * rather than showing a frozen screen.
 */
export class LiveSocket {
  private socket: WebSocket | null = null;
  private retry = 0;
  private closed = false;
  private timer: number | null = null;

  constructor(
    private readonly characterId: number,
    private readonly onState: (character: CharacterView, settlement: Settlement) => void,
    private readonly onStatus: (status: 'connecting' | 'open' | 'closed') => void,
  ) {
    this.connect();
  }

  private connect(): void {
    if (this.closed) return;
    this.onStatus('connecting');

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${location.host}/ws`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.retry = 0;
      this.onStatus('open');
      socket.send(JSON.stringify({
        type: 'subscribe',
        token: storedToken(),
        characterId: this.characterId,
      }));
    });

    socket.addEventListener('message', (event) => {
      const message = JSON.parse(String(event.data)) as {
        type: string;
        character?: CharacterView;
        settlement?: Settlement;
        drop?: WorldDropView;
      };
      if (message.type === 'state' && message.character) {
        this.onState(message.character, message.settlement ?? { elapsedSeconds: 0, stoppedBecause: null });
      }
      if (message.type === 'world_drop' && message.drop) pushWorldDrop(message.drop);
      // Trade between players (request, session, end).
      if (isTradeEvent(message)) pushTradeEvent(message);
    });

    socket.addEventListener('close', () => {
      this.onStatus('closed');
      if (this.closed) return;
      const delay = Math.min(30_000, 500 * 2 ** this.retry);
      this.retry += 1;
      this.timer = window.setTimeout(() => this.connect(), delay);
    });
  }

  close(): void {
    this.closed = true;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.socket?.close();
  }
}
